import React from 'react';
import { Box, Grid, Card, CardContent, Typography } from '@mui/material';

const styles = [
  {
    id: 'end-to-end',
    label: 'End to end',
    svg: (
      <svg width="220" height="60" viewBox="0 0 220 60">
        <circle cx="40" cy="30" r="8" fill="#6495c8" />
        <circle cx="180" cy="30" r="8" fill="#6495c8" />
        <line x1="48" y1="30" x2="172" y2="30" stroke="#6495c8" strokeWidth="3" />
      </svg>
    ),
    allowedCableTypes: ['audio', 'power']
  },
  {
    id: 'wye',
    label: 'Wye (Y)',
    svg: (
      <svg width="220" height="60" viewBox="0 0 220 60">
        <circle cx="50" cy="30" r="8" fill="#6495c8" />
        <circle cx="170" cy="18" r="8" fill="#6495c8" />
        <circle cx="170" cy="42" r="8" fill="#6495c8" />
        <path d="M58 30 Q80 30 100 30 Q120 18 162 18" stroke="#6495c8" strokeWidth="3" fill="none" />
        <path d="M58 30 Q80 30 100 30 Q120 42 162 42" stroke="#6495c8" strokeWidth="3" fill="none" />
      </svg>
    ),
    allowedCableTypes: ['audio']
  },
  {
    id: 'zip',
    label: 'Hybrid (Power + Audio)',
    svg: (
      <svg width="220" height="80" viewBox="0 0 220 80">
        {/* Left connectors - 2 cables */}
        <circle cx="50" cy="25" r="7" fill="#6495c8" />      {/* Power - black (top) */}
        <circle cx="50" cy="55" r="7" fill="#000" />   {/* Audio - blue (bottom) */}
        
        {/* Right connectors - 2 connectors (1-to-1) */}
        <circle cx="170" cy="25" r="7" fill="#6495c8" />     {/* Power */}
        <circle cx="170" cy="55" r="7" fill="#000" />  {/* Audio */}
        
        {/* Central junction bar */}
        <rect x="95" y="35" width="30" height="10" rx="4" fill="#666" />
        
        {/* Cable paths - 2 distinct colors and thicknesses */}
        {/* Power - Black, thick (top) */}
        <path d="M57 25 Q80 25 95 40 H125 Q140 25 163 25" stroke="#6495c8" strokeWidth="5" fill="none" />
        
        {/* Audio - Blue, thin (bottom) */}
        <path d="M57 55 Q80 55 95 40 H125 Q140 55 163 55" stroke="#000" strokeWidth="3" fill="none" />
      </svg>
    ),
    allowedCableTypes: ['hybrid']
  },
  {
    id: 'zip-audio-audio',
    label: 'Zip (Audio + Audio)',
    svg: (
      <svg width="220" height="80" viewBox="0 0 220 80">
        {/* Left connectors - 2 audio cables */}
        <circle cx="50" cy="25" r="7" fill="#6495c8" />  {/* Audio 1 - light blue */}
        <circle cx="50" cy="55" r="7" fill="#4caf50" />  {/* Audio 2 - green */}
        
        {/* Right connectors - 2 connectors (1-to-1) */}
        <circle cx="170" cy="25" r="7" fill="#6495c8" />  {/* Audio 1 */}
        <circle cx="170" cy="55" r="7" fill="#4caf50" />  {/* Audio 2 */}
        
        {/* Central junction bar */}
        <rect x="95" y="35" width="30" height="10" rx="4" fill="#666" />
        
        {/* Cable paths - 2 distinct colors, both thin (audio) */}
        {/* Audio 1 - Light blue, thin */}
        <path d="M57 25 Q80 25 95 40 H125 Q140 25 163 25" stroke="#6495c8" strokeWidth="3" fill="none" />
        
        {/* Audio 2 - Green, thin */}
        <path d="M57 55 Q80 55 95 40 H125 Q140 55 163 55" stroke="#4caf50" strokeWidth="3" fill="none" />
      </svg>
    ),
    allowedCableTypes: ['audio']
  },
  {
    id: 'hybrid-power-audio-audio',
    label: 'Hybrid (Power + Audio + Audio)',
    svg: (
      <svg width="220" height="100" viewBox="0 0 220 100">
        {/* Left connectors - 3 cables */}
        <circle cx="40" cy="20" r="7" fill="#6495c8" />      {/* Power - black (top) */}
        <circle cx="40" cy="50" r="7" fill="#000" />   {/* Audio 1 - blue (middle) */}
        <circle cx="40" cy="80" r="7" fill="#4caf50" />   {/* Audio 2 - green (bottom) */}
        
        {/* Right connectors - 3 connectors (1-to-1) */}
        <circle cx="180" cy="20" r="7" fill="#6495c8" />     {/* Power */}
        <circle cx="180" cy="50" r="7" fill="#000" />  {/* Audio 1 */}
        <circle cx="180" cy="80" r="7" fill="#4caf50" />  {/* Audio 2 */}
        
        {/* Central junction bar */}
        <rect x="95" y="45" width="30" height="10" rx="4" fill="#666" />
        
        {/* Cable paths - 3 distinct colors and thicknesses */}
        {/* Power - Black, thick (top) */}
        <path d="M47 20 Q75 20 95 50 H125 Q145 20 173 20" stroke="#6495c8" strokeWidth="5" fill="none" />
        
        {/* Audio 1 - Blue, thin (middle) */}
        <path d="M47 50 Q75 50 95 50 H125 Q145 50 173 50" stroke="#000" strokeWidth="3" fill="none" />
        
        {/* Audio 2 - Green, thin (bottom) */}
        <path d="M47 80 Q75 80 95 50 H125 Q145 80 173 80" stroke="#4caf50" strokeWidth="3" fill="none" />
      </svg>
    ),
    allowedCableTypes: ['hybrid']
  }
];

function StyleSelection({ value, onChange, cableType }) {
  const availableStyles = styles.filter(style => 
    style.allowedCableTypes.includes(cableType)
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" gutterBottom>
        Select Cable Style
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        {cableType === 'power' ? 'Power cables can only be end to end' :
         cableType === 'hybrid' ? 'Hybrid cables use the zip style to combine power and audio' :
         'Choose the style of cable you want to build'}
      </Typography>
      <Grid container spacing={4} justifyContent="center">
        {availableStyles.map((style) => (
          <Grid item xs={12} md={4} key={style.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                border: value === style.id ? 2 : 0,
                borderColor: 'primary.main',
                '&:hover': {
                  boxShadow: 6
                }
              }}
              onClick={() => {
                onChange(style.id);
              }}
            >
              <Box sx={{ mt: 2, mb: 1 }}>{style.svg}</Box>
              <CardContent>
                <Typography variant="subtitle1" align="center">
                  {style.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default StyleSelection; 